import React, { useState, useEffect } from 'react';
import { 
  ExclamationTriangleIcon,
  TrashIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

/**
 * DeletionConfirmModal component for confirming deletion actions
 * Shows different UI for admin vs user deletions
 */
const DeletionConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  isAdmin = false,
  contentType = 'content',
  contentTitle = 'this content',
  isLoading = false
}) => {
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState('');

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setReason('');
      setReasonError('');
    }
  }, [isOpen]);

  // Don't render if not open
  if (!isOpen) {
    return null;
  }

  const handleConfirm = () => {
    // Validate reason for admin deletions
    if (isAdmin && !reason.trim()) {
      setReasonError('Please provide a reason for deletion');
      return;
    }

    if (reason.length > 500) {
      setReasonError('Reason must be 500 characters or less');
      return;
    }

    setReasonError('');
    onConfirm(reason.trim());
  };

  const handleReasonChange = (e) => {
    setReason(e.target.value);
    if (reasonError) {
      setReasonError('');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape' && !isLoading) {
      onClose();
    }
  };

  const modalTitle = isAdmin 
    ? `Delete ${contentType} as moderator`
    : `Delete your ${contentType}`;

  const warningText = isAdmin
    ? `You are about to permanently delete this ${contentType}. This action cannot be undone and will be logged for audit purposes.`
    : `Are you sure you want to delete ${contentTitle}? This action cannot be undone.`;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-full ${
              isAdmin 
                ? 'bg-red-100 dark:bg-red-900/30' 
                : 'bg-yellow-100 dark:bg-yellow-900/30'
            }`}>
              <ExclamationTriangleIcon className={`w-6 h-6 ${
                isAdmin 
                  ? 'text-red-600 dark:text-red-400' 
                  : 'text-yellow-600 dark:text-yellow-400'
              }`} />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {modalTitle}
            </h3>
          </div>
          
          <button
            onClick={onClose}
            disabled={isLoading}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            aria-label="Close modal"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-gray-600 dark:text-gray-300 mb-4">
            {warningText}
          </p>

          {/* Admin reason input */}
          {isAdmin && (
            <div className="mb-4">
              <label 
                htmlFor="deletion-reason" 
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Reason for deletion <span className="text-red-500">*</span>
              </label>
              <textarea
                id="deletion-reason"
                value={reason}
                onChange={handleReasonChange}
                placeholder="Enter the reason for deleting this content..."
                className={`w-full p-3 border rounded-lg resize-none
                  ${reasonError 
                    ? 'border-red-300 focus:ring-red-500 focus:border-red-500' 
                    : 'border-gray-300 dark:border-gray-600 focus:ring-purple-500 focus:border-purple-500'
                  }
                  bg-white dark:bg-gray-700 text-gray-900 dark:text-white
                  focus:outline-none focus:ring-2
                `}
                rows={3}
                maxLength={500}
                disabled={isLoading}
              />
              
              {/* Character count */}
              <div className="flex justify-between items-center mt-1">
                {reasonError && (
                  <p className="text-sm text-red-600 dark:text-red-400">
                    {reasonError}
                  </p>
                )}
                <p className="text-xs text-gray-500 dark:text-gray-400 ml-auto">
                  {reason.length}/500
                </p>
              </div>
            </div>
          )}

          {/* Content preview */}
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 mb-4">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              <span className="font-medium">Content to delete:</span>
            </p>
            <p className="text-sm text-gray-800 dark:text-gray-200 mt-1 truncate">
              {contentTitle}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 
                       bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 
                       border border-gray-300 dark:border-gray-600 rounded-lg 
                       transition-colors duration-200 focus:outline-none focus:ring-2 
                       focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          
          <button
            onClick={handleConfirm}
            disabled={isLoading || (isAdmin && !reason.trim())}
            className={`px-4 py-2 text-sm font-medium text-white rounded-lg 
                       transition-colors duration-200 focus:outline-none focus:ring-2 
                       focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed
                       inline-flex items-center space-x-2
                       ${isAdmin 
                         ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500' 
                         : 'bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500'
                       }`}
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Deleting...</span>
              </>
            ) : (
              <>
                <TrashIcon className="w-4 h-4" />
                <span>Delete {contentType}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeletionConfirmModal;